<!--
  - Copyright (c) 2022-2023. Haodong JU
  -->

<template>
  <large-base-card>
    <div class="d-flex align-center">
      <div>
        <div class="text-h4 font-weight-bold">
          {{ $t('PaymentMethods') }}
        </div>
        <div class="text-body-2 text-subtitle-1">
          {{ $t('PaymentDashboardHint') }}
        </div>
      </div>

      <v-spacer />
      <div
        class="d-flex align-center"
        style="height: 48px;"
      >
        <v-icon>mdi-cash-multiple</v-icon>
      </div>
    </div>
    <div
      class="mt-4"
      style="height: 280px;overflow-y: scroll"
    >
      <loading-provider :loading="needReload">
        <template v-if="filledPaidInfo.length > 0">
          <v-card
            class="mb-2 pa-0 text-body-2"
            elevation="0"
            style="position:relative;"
            tile
          >
            <div
              v-for="p in filledPaidInfo"
              :key="p.id"
              style="z-index: 1"
            >
              <div class="d-flex align-center">
                <div class="text-body-1">
                  {{ p.paidName }}
                </div>
                <v-spacer />
                <div class="d-flex align-center mt-2 text-body-2">
                  {{ p.paidTotal | priceDisplay }}({{ p.paidCount }})
                </div>
              </div>

              <v-card
                :style="{width:p.paidPercentage+'%'}"
                class="mt-2"
                elevation="0"
                style="height: 16px;background:rgba(76,175,80,0.26);z-index: 0"
              />
            </div>
            <div
              v-if="tipsPaidInfo.paidCount > 0"
              style="z-index: 1"
            >
              <div class="d-flex align-center">
                <div class="text-body-1">
                  {{ tipsPaidInfo.paidName }}
                </div>
                <v-spacer />
                <div class="d-flex align-center mt-2 text-body-2">
                  {{ tipsPaidInfo.paidTotal | priceDisplay }}({{ tipsPaidInfo.paidCount }})
                </div>
              </div>
              <v-card
                :style="{width:tipsPaidInfo.paidPercentage}"
                class="mt-2"
                elevation="0"
                style="height: 16px;background:#e3f2fd;z-index: 0"
              />
            </div>
          </v-card>
        </template>
        <template v-else>
          <v-card
            class="d-flex justify-center align-center"
            elevation="0"
            height="100%"
          >
            <div>
              <div class="d-flex align-center justify-center flex-wrap">
                <div class="mt-2">
                  <v-icon
                    class="order-0"
                    color="#eee"
                    size="125px"
                  >
                    mdi-file-document-alert
                  </v-icon>
                </div>
                <div
                  :style="$vuetify.breakpoint.smAndUp ? $vuetify.breakpoint.mdAndUp ? 'width: 80%' : 'width: 70%' : 'width: 90%'"
                  class="mt-2"
                >
                  <div>{{ $t('noDataNow') }}</div>
                </div>
              </div>
            </div>
          </v-card>
        </template>
      </loading-provider>
    </div>
  </large-base-card>
</template>

<script>
import { comparator } from '@/model/dataLayer/service/DashboardService'
import LargeBaseCard from '@/views/widget/commonDecoration/LargeBaseCard.vue'
import { groupBy, uniqBy } from 'lodash'
import { getAllDeviceIdPaymentList, getFlattenedArray } from '@/model/dataLayer/service/MultipleStores'
import LoadingProvider from '@/views/widget/LoadingProvider'

export default {
  components: {
    LoadingProvider,
    LargeBaseCard
  },
  props: {
    info: {}
  },
  data: function () {
    return {
      needReload: false,
      paymentMethodList: [],
      paidInfo: []
    }
  },
  computed: {
    filledPaidInfo () {
      const totalPayment = this.paidInfo.filter(it => it.id !== '9').reduce((sum, i) => sum + parseFloat(i.paidTotal), 0)
      return this.paymentMethodList.filter(p => p.id > 0).map(p => {
        const r = this.paidInfo.find(t => t.id === p.id)
        return {
          id: p.id,
          paidName: r?.paidName,
          paidCount: r?.paidCount ?? 0,
          paidTotal: r?.paidTotal ?? 0,
          paidPercentage: (r?.paidTotal ?? 0) / totalPayment * 100
        }
      }).filter(it => it.paidTotal > 0).sort((a, b) => comparator(b.paidCount, a.paidCount))
    },
    tipsPaidInfo () {
      const res = this.paymentMethodList.find(p => p.id === '9')
      const r = this.paidInfo.find(t => t.id === '9')
      return {
        paidName: res?.name ?? r?.paidName ?? '',
        paidCount: r?.paidCount ?? 0,
        paidTotal: Math.abs(r?.paidTotal ?? 0),
        paidPercentage: 100
      }
    }
  },
  watch: {
    info: {
      immediate: true,
      deep: true,
      handler (val) {
        this.loadData()
      }
    }
  },
  async mounted () {
    await this.loadData()
  },
  methods: {
    async loadData () {
      this.needReload = true
      const paymentList = ((await Promise.allSettled(this.info?.url?.map(getAllDeviceIdPaymentList) ?? [])).map(it => it.value).flat()).map(it => {
        it.name = it.langs[1].name
        return it
      })
      // 多店汇总时同一支付方式会出现多次，按 id 去重；按名称去重会把同名但 id 不同的支付方式（及其收款）丢掉
      this.paymentMethodList = uniqBy(paymentList, 'id')
      const realPaidInfo = getFlattenedArray(this.info, 'paidInfo')
      const groupInfo = groupBy(realPaidInfo, 'paidName')
      this.paidInfo = Object.values(groupInfo).map(arr => {
        return arr.reduce((b, c) => {
          b.paidTotal += parseFloat(c.paidTotal)
          b.paidCount += parseFloat(c.paidCount)
          return b
        }, { id: arr[0].id, paidName: arr[0].paidName, paidTotal: 0, paidCount: 0 })
      })
      this.needReload = false
    }
  }
}
</script>
